import { ApiEndpoint } from '@/api/registry';
import { useApiQuery } from '@/api/useApiQuery';
import type { ModelDataForecastResponse, ModelOutputTarget } from '@/features/forecasts/forecasts.api.types';
import { useTenant } from '@/providers/TenantProvider';

type Params = {
  runId: string | null;
  fileId: string;
  primaryKeys: Record<string, string>;
  groupId?: string;
  leadtimeSpans?: Array<[number, number]>;
  enabled?: boolean;
};

export function useTargetSLQuantiles({
  runId,
  fileId,
  primaryKeys,
  groupId = 'default',
  leadtimeSpans,
  enabled = true,
}: Params) {
  const { tenantAlias } = useTenant();

  const result = useApiQuery<ModelDataForecastResponse>(
    tenantAlias,
    ApiEndpoint.ModelOutputsForecast,
    {
      body: {
        run_id: runId ?? '',
        group_id: groupId,
        file_id: fileId,
        primary_keys: primaryKeys,
        ...(leadtimeSpans !== undefined ? { leadtime_spans: leadtimeSpans } : {}),
      },
    },
    { enabled: enabled && Boolean(runId) },
  );

  const results = result.data?.results ?? [];

  // Full quantile grid the API serves — mirrors the backend's empirical quantile
  // demand model. Levels absent for this run are filtered out.
  const QUANTILE_FIELDS: Array<[number, keyof ModelOutputTarget]> = [
    [0.05, 'quantile_5'],
    [0.1, 'quantile_10'],
    [0.2, 'quantile_20'],
    [0.3, 'quantile_30'],
    [0.4, 'quantile_40'],
    [0.5, 'quantile_50'],
    [0.6, 'quantile_60'],
    [0.7, 'quantile_70'],
    [0.75, 'quantile_75'],
    [0.8, 'quantile_80'],
    [0.85, 'quantile_85'],
    [0.9, 'quantile_90'],
    [0.93, 'quantile_93'],
    [0.95, 'quantile_95'],
    [0.98, 'quantile_98'],
  ];
  const grid = QUANTILE_FIELDS.map(([p, key]) => ({
    p,
    values: results.map((r) => (r.target_output[key] ?? null) as number | null),
  })).filter((level) => level.values.some((v) => v != null));

  return {
    results,
    spans: results.map((r) => r.span),
    timestamps: results.map((r) => r.timestamp.slice(0, 10)),
    mean: results.map((r) => r.target_output.mean),
    quantile80: results.map((r) => r.target_output.quantile_80 ?? null),
    quantile90: results.map((r) => r.target_output.quantile_90 ?? null),
    quantile95: results.map((r) => r.target_output.quantile_95 ?? null),
    grid,
    isLoading: result.isLoading,
    error: result.error,
  };
}
